import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import ZoomVideo from "@zoom/videosdk";
import type { MediaStream } from "../../index-types";

type JoinArgs = {
  topic: string;
  name: string;
  signature: string;
  password?: string;
  webEndpoint?: string;
};

type ZoomStatus =
  | "idle"
  | "inited"
  | "joining"
  | "joined"
  | "left"
  | "error";

type ZoomState = {
  client: any;
  mediaStream: MediaStream | null;
  status: ZoomStatus;
  initOnce: (webEndpoint?: string) => Promise<void>;
  joinOnce: (args: JoinArgs) => Promise<void>;
  leave: () => Promise<void>;
};

const Ctx = createContext<ZoomState | null>(null);

export function ZoomProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const clientRef = useRef<any>(ZoomVideo.createClient());

  const [mediaStream, setMediaStream] =
    useState<MediaStream | null>(null);

  const [status, setStatus] =
    useState<ZoomStatus>("idle");

  const initPromiseRef =
    useRef<Promise<void> | null>(null);

  const joinPromiseRef =
    useRef<Promise<void> | null>(null);

  const initOnce = useCallback(
    async (webEndpoint?: string) => {
      if (initPromiseRef.current) {
        return initPromiseRef.current;
      }

      initPromiseRef.current = (async () => {
        const client = clientRef.current;

        /*
         * Do not initialise the same client more than once.
         */
        if (
          status === "joined" ||
          status === "joining" ||
          status === "inited"
        ) {
          return;
        }

        const requirements =
          ZoomVideo.checkSystemRequirements();

        if (!requirements.video) {
          throw new Error(
            "This browser does not support Zoom video."
          );
        }

        if (!requirements.audio) {
          throw new Error(
            "This browser does not support Zoom audio."
          );
        }

        await client.init(
          "en-US",
          "Global",
          {
            webEndpoint:
              webEndpoint ??
              (window as any).webEndpoint ??
              "zoom.us",
            stayAwake: true,
            patchJsMedia: true,
            leaveOnPageUnload: true,
            enableLog: false,
            enforceMultipleVideos: true,
          }
        );

        setStatus("inited");
      })().catch((error) => {
        console.error("Zoom client init failed", error);

        initPromiseRef.current = null;
        setStatus("error");

        throw error;
      });

      return initPromiseRef.current;
    },
    [status]
  );

  const joinOnce = useCallback(
    async (args: JoinArgs) => {
      if (joinPromiseRef.current) {
        return joinPromiseRef.current;
      }

      joinPromiseRef.current = (async () => {
        await initOnce(args.webEndpoint);

        setStatus("joining");

        await clientRef.current.join(
          args.topic,
          args.signature,
          args.name,
          args.password || ""
        );

        const stream =
          clientRef.current.getMediaStream();

        setMediaStream(stream);
        setStatus("joined");
      })().catch((error) => {
        console.error("Zoom client join failed", error);

        joinPromiseRef.current = null;
        setMediaStream(null);
        setStatus("error");

        throw error;
      });

      return joinPromiseRef.current;
    },
    [initOnce]
  );

  const leave = useCallback(async () => {
    try {
      const sessionInfo =
        clientRef.current?.getSessionInfo?.();

      if (sessionInfo?.isInMeeting) {
        await clientRef.current.leave();
      }
    } finally {
      setMediaStream(null);
      setStatus("left");
      joinPromiseRef.current = null;
    }
  }, []);

  const value = useMemo<ZoomState>(
    () => ({
      client: clientRef.current,
      mediaStream,
      status,
      initOnce,
      joinOnce,
      leave,
    }),
    [
      mediaStream,
      status,
      initOnce,
      joinOnce,
      leave,
    ]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
    </Ctx.Provider>
  );
}

export function useZoom() {
  const value = useContext(Ctx);

  if (!value) {
    throw new Error(
      "useZoom must be used within ZoomProvider"
    );
  }

  return value;
}